import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CustomSelect from 'components/form/CustomSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  TicketCategory,
  TicketTypeSummaryResult,
  updateTicketType,
} from 'stores/TicketTypeStoreAdapter';
import { successDefault, toErrorDetail } from 'utils/Toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
  type: TicketTypeSummaryResult;
};

const RESERVED_PREFIX = ['PRV', 'REQ', 'CONT'];

/**
 * チケット種別の基本情報を編集するダイアログ（API7004・UC-TK04）。
 * - スコープ（グローバル/プライベート）は作成時に確定し、編集ではいかなる場合も変更不可
 * - 未有効化（hasBeenActivated=false）: 種別名 / 分類 / キープレフィックス / 表示順 を編集可
 * - 無効化（有効化後 is_active=false）: 種別名 / 表示順 のみ編集可（非正規化元は変更不可）
 * - 有効中は本ダイアログを開かない（呼び出し側でガード）
 */
const TicketTypeEditDialog: React.FC<Props> = ({ open, onClose, onUpdated, type }) => {
  // 未有効化のみ非正規化元（scope/category/keyPrefix）を編集できる
  const denormEditable = !type.hasBeenActivated;

  const [name, setName] = useState(type.name);
  // スコープ（グローバル/プライベート）は種別の作成時に確定し、編集ではいかなる場合も変更不可
  const scope = type.scope;
  const [category, setCategory] = useState<TicketCategory>(type.ticketCategory);
  const [keyPrefix, setKeyPrefix] = useState(type.keyPrefix ?? '');
  const [sortOrder, setSortOrder] = useState(String(type.sortOrder));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(type.name);
    setCategory(type.ticketCategory);
    setKeyPrefix(type.keyPrefix ?? '');
    setSortOrder(String(type.sortOrder));
    setErrors({});
  }, [open, type]);

  const isGlobal = scope === 'GLOBAL';

  // プライベートは分類TASK固定。スコープ自体は変更不可のため、分類はグローバル時のみ選択可
  const handleCategoryChange = (v: string) => {
    setCategory(v as TicketCategory);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = MSG_REQUIRED('種別名');
    else if (name.length > 200) e.name = MSG_MAX_LENGTH('種別名', 200);
    if (denormEditable && isGlobal) {
      if (!keyPrefix.trim()) e.keyPrefix = MSG_REQUIRED('キープレフィックス');
      else if (!/^[A-Za-z]{1,5}$/.test(keyPrefix))
        e.keyPrefix = '英字最大5文字で入力してください。';
      else if (RESERVED_PREFIX.includes(keyPrefix.toUpperCase()))
        e.keyPrefix = 'PRV / REQ / CONT は使用できません。';
    }
    if (sortOrder && !/^-?\d+$/.test(sortOrder)) e.sortOrder = '数値で入力してください。';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdate = () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    updateTicketType({
      typeId: type.typeId,
      name: name.trim(),
      // スコープは編集不可のため常に送らない。非正規化元（分類/プレフィックス）は未有効化時のみ送る
      ticketCategory: denormEditable ? category : undefined,
      keyPrefix: denormEditable && isGlobal ? keyPrefix.trim() : undefined,
      sortOrder: sortOrder ? Number(sortOrder) : undefined,
    })
      .then(() => {
        successDefault('種別を更新しました');
        onUpdated();
        onClose();
      })
      .catch((err) => setErrors({ submit: toErrorDetail(err) ?? '更新に失敗しました' }))
      .finally(() => setSubmitting(false));
  };

  const categoryOptions = [
    { value: 'TASK', label: 'タスク' },
    { value: 'APPROVAL', label: '承認' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          種別の編集
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!denormEditable && (
            <Alert severity="info">
              有効化したことがある種別のため、スコープ・分類・キープレフィックスは変更できません。
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            required
            label="種別名"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {denormEditable && (
            <CustomSelect
              mode="single"
              label="分類"
              options={categoryOptions}
              value={category}
              onChange={handleCategoryChange}
              disabled={scope === 'PRIVATE'}
            />
          )}
          {denormEditable && isGlobal && (
            <TextField
              size="small"
              fullWidth
              required
              label="キープレフィックス"
              value={keyPrefix}
              onChange={(e) => setKeyPrefix(e.target.value.toUpperCase())}
              error={!!errors.keyPrefix}
              helperText={errors.keyPrefix ?? '英字最大5文字（PRV/REQ/CONT 不可）'}
              slotProps={{ htmlInput: { maxLength: 5 } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}

          <TextField
            size="small"
            fullWidth
            label="表示順"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            error={!!errors.sortOrder}
            helperText={errors.sortOrder}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={handleUpdate}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              更新
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default TicketTypeEditDialog;
